import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Loader2 } from "lucide-react";

export type ActionStatus = "idle" | "pending" | "success" | "error";

const SETTLE_MS = { success: 1600, error: 2600 } as const;

/**
 * Runs an async action and reports its lifecycle. Success and error states
 * settle back to idle on their own; a newer run always wins.
 */
export function useActionStatus() {
  const [status, setStatus] = useState<ActionStatus>("idle");
  const timer = useRef<number | undefined>(undefined);
  const runId = useRef(0);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const settle = useCallback((next: "success" | "error") => {
    setStatus(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setStatus("idle"), SETTLE_MS[next]);
  }, []);

  const run = useCallback(async (action: () => Promise<boolean | void>) => {
    const id = ++runId.current;
    window.clearTimeout(timer.current);
    setStatus("pending");
    try {
      const ok = (await action()) !== false;
      if (id === runId.current) settle(ok ? "success" : "error");
      return ok;
    } catch (err) {
      if (id === runId.current) settle("error");
      throw err;
    }
  }, [settle]);

  return { status, run, settle };
}

export default function ActionButton({
  status,
  icon,
  label,
  pendingLabel,
  successLabel,
  errorLabel = "Try again",
  iconOnly = false,
  className = "",
  disabled,
  ...rest
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  status: ActionStatus;
  icon: React.ReactNode;
  label: string;
  pendingLabel: string;
  successLabel: string;
  errorLabel?: string;
  iconOnly?: boolean;
}) {
  const labels: Record<ActionStatus, string> = {
    idle: label,
    pending: pendingLabel,
    success: successLabel,
    error: errorLabel,
  };
  return (
    <>
    <button
      type="button"
      {...rest}
      disabled={disabled || status === "pending"}
      aria-busy={status === "pending" || undefined}
      aria-label={iconOnly ? labels[status] : rest["aria-label"]}
      className={`action-button is-${status} ${className}`}
    >
      <span className="action-button-icon" aria-hidden="true">
        <span data-slot="idle">{icon}</span>
        <span data-slot="pending"><Loader2 size={16} className="animate-spin" /></span>
        <span data-slot="success"><Check size={16} strokeWidth={2.5} /></span>
        <span data-slot="error"><AlertCircle size={16} /></span>
      </span>
      {!iconOnly && (
        <span className="action-button-label">
          {(Object.keys(labels) as ActionStatus[]).map((key) => (
            <span key={key} data-slot={key} aria-hidden={key !== status}>
              {labels[key]}
            </span>
          ))}
        </span>
      )}
    </button>
    <span className="sr-only" aria-live="polite">
      {status === "success" || status === "error" ? labels[status] : ""}
    </span>
    </>
  );
}
